/**
 * Loads all namespace JSON files for the given locale and deep-merges them
 * into the `{ namespace: keyValueMap }` tree that use-intl consumes.
 *
 * Layout convention:
 *   src/i18n/locales/{vi,en}/<file>.json
 *   src/i18n/locales/{vi,en}/<group>/<file>.json   (one level of subfolder OK)
 *
 * Each JSON file's top-level key IS the namespace name (e.g. "auth",
 * "common"). Multiple files MAY share the same top-level key — the loader
 * does a recursive deep merge so `auth/signin.json` and `auth/signup.json`
 * cohabit cleanly under the unified `auth.*` tree without overwriting each
 * other.
 *
 * Vite splits each file into its own async chunk so they download in
 * parallel on first paint; only one locale's set is ever fetched.
 */

import type { Locale } from "./locale-cookie";

const VI_MODULES = import.meta.glob("./locales/vi/**/*.json");
const EN_MODULES = import.meta.glob("./locales/en/**/*.json");

type JsonObject = Record<string, unknown>;

function isPlainObject(v: unknown): v is JsonObject {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Recursive deep merge — `source` properties win on leaf collisions.
 * Used so a tag-namespace and a settings-namespace can each contribute keys
 * to the merged tree without trampling sibling files.
 */
function deepMerge(target: JsonObject, source: JsonObject): JsonObject {
  for (const [key, value] of Object.entries(source)) {
    const prev = target[key];
    if (isPlainObject(prev) && isPlainObject(value)) {
      target[key] = deepMerge({ ...prev }, value);
    } else {
      target[key] = value;
    }
  }
  return target;
}

async function mergeModules(
  modules: Record<string, () => Promise<unknown>>
): Promise<JsonObject> {
  const loaded = await Promise.all(
    Object.values(modules).map(async (load) => {
      const mod = (await load()) as { default: JsonObject };
      return mod.default;
    })
  );

  let merged: JsonObject = {};
  for (const content of loaded) {
    merged = deepMerge(merged, content);
  }
  return merged;
}

export async function loadMessages(locale: Locale): Promise<JsonObject> {
  const modules = locale === "vi" ? VI_MODULES : EN_MODULES;
  return mergeModules(modules);
}
